import { useCallback, useState } from "react"
import { SectionInput } from "../components/ScopeWorkflowForm/angular/AddScopeWorkflowForm.types"
import SectionType from "../enums/sectionType"
import { ASSET_TYPES, MAX_PATCH_SECTION_SIZE, MAX_PIPE_DIAMETER, MAX_PIPE_LENGTH, MAX_SCOPE_WORKFLOW_SECTIONS, MAX_SECTION_LENGTH, MAX_TEMP, MAX_THICKNESS, MIN_PATCH_SECTION_SIZE, MIN_PIPE_DIAMETER, MIN_PIPE_LENGTH, MIN_SECTION_LENGTH, MIN_TEMP, MIN_THICKNESS, PATCH_SCAN_POSITION_INCREMENT } from "../utils/constants";
import { AngularScopeWorkflow, PatchScopeWorkflow } from "../types/starAPIResponseTypes"
import { anyMoreThanOnce, floatMoreThanOnce, intMoreThanOnce, possibleNegativefloatMoreThanOnce } from "../utils/validation";
import { showErrorToast } from "../utils/utils";
import PatchCornerType from "../enums/patchCornerTypes";

type usePatchScopeWorkflowInputsProps = {
  locations: string[];
  insulationMaterials: string[];
  claddingMaterials: string[];
  currentSection: number;
}

const usePatchScopeWorkflowInputs = (props: usePatchScopeWorkflowInputsProps) => {

  const {
    locations,
    insulationMaterials,
    claddingMaterials,
    currentSection
  } = props

  const [filename, setFilename] = useState<string>("")
  const [filenameError, setFilenameError] = useState<Error|null>(null)

  const [assetId, setAssetId] = useState<string>("")
  const [assetIdError, setAssetIdError] = useState<Error|null>(null)
  const [assetTemperature, setAssetTemperature] = useState<string>("")
  const [assetTemperatureError, setAssetTemperatureError] = useState<Error|null>(null)

  const [assetType, setAssetType] = useState<number>(0)
  const assetTypes = ASSET_TYPES.map(asset => asset.name)

  const [insulationMaterial, setInsulationMaterial] = useState<number>(0)

  const [insulationThickness, setInsulationThickness] = useState<string>("")
  const [insulationThicknessError, setInsulationThicknessError] = useState<Error|null>(null)

  const [claddingMaterial, setCladdingMaterial] = useState<number>(0)

  const [claddingThickness, setCladdingThickness] = useState<string>("")
  const [claddingThicknessError, setCladdingThicknessError] = useState<Error|null>(null)

  const [location, setLocation] = useState<number>(0)

  const [sectionWidths, setSectionWidths] = useState<SectionInput[]>([])
  const [sectionHeights, setSectionHeights] = useState<SectionInput[]>([])

  const [sectionPositions, setSectionPositions] = useState<number[][]>([])

  const [cornerType, setCornerType] = useState<number[]>([])
  const cornerTypes = Object.values(PatchCornerType)

  const onChangeSectionWidth = (value: string, error: Error|null, sectionIndex?: number) => {
    setSectionWidths(sectionWidths.map((start, i) => {

      const sectionToCheck = sectionIndex !== undefined ? sectionIndex : currentSection
      if (i === sectionToCheck) {
        return { value: value, error: error }
      }
      return start
    }))
  }

  const onChangeSectionHeight = (value: string, error: Error|null, sectionIndex?: number) => {
    setSectionHeights(sectionHeights.map((end, i) => { 

      const sectionToCheck = sectionIndex !== undefined ? sectionIndex : currentSection
      if (i === sectionToCheck) {
        return { value: value, error: error }
      }
      return end
    }))
  }

  const onChangeCornerType = (value: number) => {
    setCornerType(cornerType.map((type, i) => {
      if (i === currentSection) {
        return value
      }
      return type
    }))
  }

  const onAddPosition = (position: number) => {
    setSectionPositions(positions => 
      positions.map((sectionPositions, i) => {
        if (i === currentSection) {
          return [...sectionPositions, position]
        }
        return sectionPositions
      })
    )
  }

  const onSetPositions = (positionsToSet: number[]) => {
    setSectionPositions(positions => 
      positions.map((sectionPositions, i) => {
        if (i === currentSection) {
          return positionsToSet
        }
        return sectionPositions
      })
    )
  }

  const setDefaultPositions = (width: number) => {    
    const numberOfPositions = Math.round(width / PATCH_SCAN_POSITION_INCREMENT)
    const sectionPositions = []

    for (let i=0; i<numberOfPositions; i++) {      
      sectionPositions.push(i*PATCH_SCAN_POSITION_INCREMENT)
    }
    onSetPositions(sectionPositions)
  }

  const onAddSectionValues = () => {
    if (sectionWidths.length >= MAX_SCOPE_WORKFLOW_SECTIONS) { return }
    // const start = sectionStarts.length === 0 ? `${workflowStart}` : `${sectionEnds[currentSection-1].value}`
    // const end = parseInt(start) + 1000
    const width = 1000
    const height = 1000

    const widthInput = {
      value: `${width}`,
      error: null
    }

    const heightInput = {
      value: isNaN(height) ? "" : `${height}`,
      error: null
    }

    const numberOfPositions = Math.round(width / PATCH_SCAN_POSITION_INCREMENT)
    const newSectionPositions = []

    for (let i=0; i<numberOfPositions; i++) {      
      newSectionPositions.push(i*PATCH_SCAN_POSITION_INCREMENT)
    }

    setSectionWidths([...sectionWidths, ...[{...widthInput}]])
    
    setSectionHeights([...sectionHeights, ...[{...heightInput}]])
    setSectionPositions([...sectionPositions, ...[newSectionPositions]])
    setCornerType([...cornerType, ...[0]])   
  }

  const onRemoveCurrentSectionInputs = useCallback(() => {  
    if (sectionWidths.length === 1) { return }
    setSectionWidths(starts => starts.filter((_, i) => i !== currentSection))
    setSectionHeights(ends => ends.filter((_, i) => i !== currentSection))
    setSectionPositions(angles => angles.filter((_, i) => i !== currentSection))
    setCornerType(cornerTypes => cornerTypes.filter((_, i) => i !== currentSection))
  }, [sectionWidths, currentSection])

  const setScopeWorkflowForm = useCallback((scopeWorkflow: PatchScopeWorkflow) => {
    setFilename(scopeWorkflow.filename)
    setAssetId(scopeWorkflow.asset_id)
    setLocation(locations.indexOf(scopeWorkflow.location))
    setAssetTemperature(scopeWorkflow.asset_temp.toString())
    setAssetType(0)
    setInsulationMaterial(insulationMaterials.indexOf(scopeWorkflow.insulation_material))
    setInsulationThickness(scopeWorkflow.insulation_thickness.toString())
    setCladdingMaterial(claddingMaterials.indexOf(scopeWorkflow.cladding_material))
    setCladdingThickness(scopeWorkflow.cladding_thickness.toString())
    let widths = []
    let heights = []
    let scopePosition = []
    const sections = JSON.parse(scopeWorkflow.sections)
    const positions = JSON.parse(scopeWorkflow.horizontal_positions)

    for (let i=0; i<sections.length; i++) {
      widths.push({ value: `${sections[i][0]}`, error: null })
      heights.push({ value: `${sections[i][1]}`, error: null })
      scopePosition.push( positions[i])
    }
    setSectionWidths(widths)
    setSectionHeights(heights)
    setSectionPositions(scopePosition)
    setCornerType(JSON.parse(scopeWorkflow.sections).map((section: number[]) => {
      return `${section[2]}`
    }))
  }, [locations, insulationMaterials, claddingMaterials])

  const clearInputs = useCallback(() => {
    setFilename("New workflow")
    setAssetId("Asset-12345")
    setLocation(0)
    setAssetTemperature("78")
    setAssetType(0)
    setInsulationMaterial(0)
    setInsulationThickness("10")
    setCladdingMaterial(0)
    setCladdingThickness("10")
    setSectionWidths([])
    setSectionHeights([])
    setSectionPositions([])
    setCornerType([])
    setAssetIdError(null)
    setAssetTemperatureError(null)
    setInsulationThicknessError(null)
    setCladdingThicknessError(null)
  }, [])

  const validateFilename = () => {
    setFilenameError(null)
    const isValid = anyMoreThanOnce(filename)
    setFilenameError(isValid ? null : new Error("Invalid filename."))
    return isValid
  }

  const validateAssetId = () => {
    setAssetIdError(null)
    const isValid = anyMoreThanOnce(assetId)
    setAssetIdError(isValid ? null : new Error("Invalid asset id."))
    return isValid
  }

  const validateAssetTemperature = () => {
    setAssetTemperatureError(null)
    let isValid = possibleNegativefloatMoreThanOnce(assetTemperature)
    setAssetTemperatureError(isValid ? null : new Error("Invalid asset temperature."))
    
    if (!isValid) { return isValid }

    const tempValue = parseFloat(assetTemperature)
    isValid = isValid && tempValue >= MIN_TEMP && tempValue < MAX_TEMP
    if (!isValid) {
      setAssetTemperatureError(new Error("Invalid asset temperature."))
    }
    return isValid
  }

  const validateCladdingThickness = () => {
    setCladdingThicknessError(null)
    let isValid = intMoreThanOnce(claddingThickness)
    setCladdingThicknessError(isValid ? null : new Error("Invalid cladding thickness."))
    

    const thicknessValue = parseFloat(claddingThickness)
    isValid = isValid && thicknessValue >= MIN_THICKNESS && thicknessValue < MAX_THICKNESS
    if (!isValid) {
      setCladdingThicknessError(new Error("Invalid cladding thickness."))
    }
    return isValid 
  }

  const validateInsulationThickness = () => {
    setInsulationThicknessError(null)
    let isValid = intMoreThanOnce(insulationThickness)
    setInsulationThicknessError(isValid ? null : new Error("Invalid insulation thickness."))
    

    const thicknessValue = parseFloat(insulationThickness)
    isValid = isValid && thicknessValue >= MIN_THICKNESS && thicknessValue < MAX_THICKNESS
    if (!isValid) {
      setInsulationThicknessError(new Error("Invalid cladding thickness."))
    }
    return isValid 
  }

  const validateSectionWidth = (sectionIndex: number) => {
    const width = sectionWidths[sectionIndex].value

    if (!intMoreThanOnce(width)) {
      onChangeSectionWidth(width, new Error("Invalid width"), sectionIndex)
      return false
    } 

    if (parseInt(width) < MIN_PATCH_SECTION_SIZE) {
      onChangeSectionWidth(width, new Error(`Start must be >= ${MIN_PATCH_SECTION_SIZE}mm`), sectionIndex)
      return false
    }

    if (parseInt(width) > MAX_PATCH_SECTION_SIZE) {
      onChangeSectionWidth(width, new Error(`Start must be <= ${MAX_PATCH_SECTION_SIZE}mm`), sectionIndex)
      return false
    }
    return true
  }

  const validateSectionHeight = (sectionIndex: number) => {
    const height = sectionHeights[sectionIndex].value

    if (!intMoreThanOnce(height)) {
      onChangeSectionHeight(height, new Error("Invalid height"), sectionIndex)
      return false
    }

    if (parseInt(height) < MIN_PATCH_SECTION_SIZE) {
      onChangeSectionHeight(height, new Error(`Height must be > ${MIN_PATCH_SECTION_SIZE}mm`), sectionIndex)
      return false
    }

    if (parseInt(height) > MAX_PATCH_SECTION_SIZE) {
      onChangeSectionHeight(height, new Error(`Height must be <= ${MAX_PATCH_SECTION_SIZE}mm`), sectionIndex)
      return false
    }

    return true
  }

  const validateSections = () => {
    if (sectionPositions.length === 0 || sectionPositions[0].length === 0) { return false }
    const numberOfSectionAngles = sectionPositions[0].length
    let validSections = true
    for (let section=0; section<sectionWidths.length; section++) {
      const validStart = validateSectionWidth(section)
      const validEnd = validateSectionHeight(section)
      
      const validSection = validStart && validEnd

      validSections = validSections && validSection

      if (!validSections) { break }
    }
    return validSections
  }

  const isValidSectionForm = () => {
    return sectionWidths.length > 0 && validateSections()
  }

  const isScopeValidWorkflowInfo = () => {
    const isValidFilename = validateFilename()
    const isValidAssetId = validateAssetId()
    const isValidAssetTemp     = validateAssetTemperature()
    const isValidCladdingThickness = validateCladdingThickness()
    const isValidInsulationThickness = validateInsulationThickness()

    const isValid = 
      isValidFilename &&
      isValidAssetId && 
      isValidAssetTemp && 
      isValidCladdingThickness &&
      isValidInsulationThickness

    return isValid
  }

  return [{
    filename: {value: filename, onChange: setFilename, error: filenameError},
    assetId: {value: assetId, onChange: setAssetId, error: assetIdError},
    location: {values: locations, onChange: setLocation, selected: location },
    assetTemperature: {value: assetTemperature, onChange: setAssetTemperature, error: assetTemperatureError},
    assetType: {values: assetTypes, onChange: setAssetType, selected: assetType},
    insulationMaterial: {values: insulationMaterials, onChange: setInsulationMaterial, selected: insulationMaterial},
    insulationThickness: {value: insulationThickness, onChange: setInsulationThickness, error: insulationThicknessError},
    claddingMaterial: {values: claddingMaterials, onChange: setCladdingMaterial, selected: claddingMaterial },
    claddingThickness: {value: claddingThickness, onChange: setCladdingThickness, error: claddingThicknessError},
    sectionWidths: { value: sectionWidths, onChange: onChangeSectionWidth },
    sectionHeights: { value: sectionHeights, onChange: onChangeSectionHeight },
    cornerTypes: { values: cornerTypes, selected: cornerType[currentSection], onChange: onChangeCornerType},
    sectionPositions: sectionPositions,
    selectedCornerType: cornerType,
    onAddPosition: onAddPosition,
    setDefaultPositions,
    onAddSectionValues,
    setScopeWorkflowForm,
    clearInputs,
    onRemoveCurrentSectionInputs,
    isScopeValidWorkflowInfo,
    isValidSectionForm
  }]
}

export default usePatchScopeWorkflowInputs